import { ComponentProps, Fragment, ReactNode, useCallback } from 'react';
import { Element } from 'react-bulma-components';
import Conditional from '../../shared/conditional/conditional';
import InfiniteScroll from '../../ui/infinite-scroll/infinite-scroll';
import PageSpinner from '../../ui/page-spinner/page-spinner';

type BaseInboxItem = {
  id: string;
};

export type AdminInboxProps<T extends BaseInboxItem> = Omit<
  ComponentProps<typeof InfiniteScroll>,
  'children'
> & {
  inboxItems: T[];
  renderInboxItem: (inboxItem: T) => ReactNode;
  header?: ReactNode;
  loadingInitialData?: boolean;
  emptyText?: string;
};

export function AdminInbox<T extends BaseInboxItem>({
  fetchMore,
  hasMore,
  inboxItems,
  renderInboxItem,
  header,
  loadingInitialData,
  emptyText = 'There are no items to show',
}: AdminInboxProps<T>) {
  const Empty = useCallback(
    () => (
      <Element
        display="flex"
        justifyContent="center"
        alignItems="center"
        className="h-full"
      >
        <span>{emptyText}</span>
      </Element>
    ),
    [emptyText]
  );

  if (inboxItems.length === 0) {
    return <Empty />;
  }

  return (
    <Conditional condition={!!loadingInitialData}>
      <Conditional.True>
        <PageSpinner />
      </Conditional.True>
      <Conditional.False>
        <InfiniteScroll fetchMore={fetchMore} hasMore={hasMore} header={header}>
          {inboxItems.map((inboxItem) => (
            <Fragment key={inboxItem.id}>{renderInboxItem(inboxItem)}</Fragment>
          ))}
        </InfiniteScroll>
      </Conditional.False>
    </Conditional>
  );
}

export default AdminInbox;
